import React, { useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './ScrollVideo.module.sass';
import { scrollVideoContent } from './ScrollVideo.content';
import { videoScrubSrc } from './ScrollVideo.asset';
import {
  getScrollVideoState,
  preloadScrollVideo,
  subscribeScrollVideo,
} from './ScrollVideo.preload';

gsap.registerPlugin(ScrollTrigger);

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Сглаживание скраба в секундах: после остановки прокрутки видео ещё «доезжает» до цели
const SCRUB = 0.7;

// Порог обновления currentTime: один кадр при 24 fps, чтобы не гонять лишние seek'и
const MIN_TIME_DELTA = 1 / 24;

// Небольшой сдвиг, чтобы браузер отрисовал первый кадр
const FIRST_FRAME_TIME = 0.001;

// Моменты появления/ухода надписей в долях прокрутки секции (0..1)
const TEXT_TIMING = {
  startOut: 0.14,
  middleIn: 0.4,
  middleOut: 0.58,
  endIn: 0.74,
};

// Длительность появления/ухода надписи в долях прокрутки и задержка между словами
const TEXT_DURATION = 0.14;
const WORD_STAGGER = 0.025;

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function Words({ text }) {
  const words = String(text).split(/\s+/).filter(Boolean);

  return words.map((word, index) => (
    <React.Fragment key={`${word}-${index}`}>
      {index > 0 ? ' ' : null}
      <span className={styles.scrollVideo__word}>
        <span className={styles.scrollVideo__wordInner} data-word="">
          {word}
        </span>
      </span>
    </React.Fragment>
  ));
}

export function ScrollVideo({
  src = videoScrubSrc,
  startText = scrollVideoContent.startText,
  middleText = scrollVideoContent.middleText,
  endText = scrollVideoContent.endText,
  ariaLabel = scrollVideoContent.ariaLabel,
}) {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const startTextRef = useRef(null);
  const middleTextRef = useRef(null);
  const endTextRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [reduced, setReduced] = useState(prefersReducedMotion);
  // Ждём blob, скачанный под прелоадером: до этого момента видео не запрашиваем повторно
  const [resolvedSrc, setResolvedSrc] = useState(() => getScrollVideoState().url);

  useLayoutEffect(() => {
    const state = getScrollVideoState();
    if (state.done) {
      setResolvedSrc(state.url || src);
      return undefined;
    }

    const unsubscribe = subscribeScrollVideo((next) => {
      // Если предзагрузка не удалась, отдаём обычную ссылку на файл
      if (next.done) setResolvedSrc(next.url || src);
    });

    preloadScrollVideo();

    return unsubscribe;
  }, [src]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event) => setReduced(event.matches);

    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const video = videoRef.current;
    if (!root || !video) return undefined;

    let mounted = true;
    let timeline = null;
    const videoState = { time: 0 };
    const ctx = gsap.context(() => {}, root);

    // Показать первый кадр сразу после метаданных, без белой вспышки
    const showFirstFrame = () => {
      const el = videoRef.current;
      if (!mounted || !el) return;
      try {
        if (el.currentTime < FIRST_FRAME_TIME) el.currentTime = FIRST_FRAME_TIME;
      } catch (e) {
        /* seek до готовности буфера может бросить исключение — игнорируем */
      }
    };

    const applyVideoTime = () => {
      const el = videoRef.current;
      if (!mounted || !el) return;

      // Не ставим новый seek в очередь, пока не завершился предыдущий — иначе рывки
      if (el.seeking) return;

      const duration = el.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const target = videoState.time;
      if (!Number.isFinite(target)) return;

      const clamped = Math.min(Math.max(target, 0), duration - FIRST_FRAME_TIME);
      if (Math.abs(el.currentTime - clamped) < MIN_TIME_DELTA) return;

      try {
        el.currentTime = clamped;
      } catch (e) {
        /* браузер может отклонить seek — пропускаем кадр */
      }
    };

    const wordsOf = (ref) => {
      const el = ref.current;
      if (!el) return null;
      const words = el.querySelectorAll('[data-word]');
      return words.length ? words : null;
    };

    const buildTimeline = () => {
      const el = videoRef.current;
      if (!mounted || !el || timeline || reduced) return;

      const duration = el.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      ctx.add(() => {
        timeline = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: root,
            start: 'top top',
            end: 'bottom bottom',
            scrub: SCRUB,
            invalidateOnRefresh: true,
          },
        });

        timeline.fromTo(
          videoState,
          { time: 0 },
          {
            time: () => {
              const current = videoRef.current;
              const value = current ? current.duration : duration;
              return Number.isFinite(value) ? value : duration;
            },
            duration: 1,
            onUpdate: applyVideoTime,
          },
          0
        );

        // Появление: слова выезжают из-под маски по очереди
        const revealWords = (ref, at) => {
          const words = wordsOf(ref);
          if (!words) return;
          timeline.fromTo(
            words,
            { yPercent: 115, autoAlpha: 0, rotate: 2 },
            {
              yPercent: 0,
              autoAlpha: 1,
              rotate: 0,
              duration: TEXT_DURATION,
              ease: 'power2.out',
              stagger: WORD_STAGGER,
            },
            at
          );
        };

        // Уход: слова уезжают вверх под маску
        const hideWords = (ref, at) => {
          const words = wordsOf(ref);
          if (!words) return;
          timeline.to(
            words,
            {
              yPercent: -115,
              autoAlpha: 0,
              rotate: -2,
              duration: TEXT_DURATION,
              ease: 'power2.in',
              stagger: WORD_STAGGER,
            },
            at
          );
        };

        hideWords(startTextRef, TEXT_TIMING.startOut);
        revealWords(middleTextRef, TEXT_TIMING.middleIn);
        hideWords(middleTextRef, TEXT_TIMING.middleOut);
        revealWords(endTextRef, TEXT_TIMING.endIn);
      });
    };

    const handleMetadata = () => {
      if (!mounted) return;
      showFirstFrame();
      setIsReady(true);
      buildTimeline();
    };

    // Safari иногда требует «разблокировать» декодирование первым жестом
    const unlockPlayback = () => {
      const el = videoRef.current;
      if (!mounted || !el) return;

      const settle = () => {
        const current = videoRef.current;
        if (!mounted || !current) return;
        current.pause();
        applyVideoTime();
      };

      try {
        const promise = el.play();
        if (promise && typeof promise.then === 'function') promise.then(settle).catch(() => {});
        else settle();
      } catch (e) {
        /* autoplay может быть запрещён — это ожидаемо */
      }
    };

    // После завершения seek'а догоняем цель, если скролл ушёл вперёд
    const handleSeeked = () => applyVideoTime();

    const refresh = () => ScrollTrigger.refresh();

    video.addEventListener('loadedmetadata', handleMetadata);
    video.addEventListener('loadeddata', showFirstFrame);
    video.addEventListener('seeked', handleSeeked);
    window.addEventListener('touchstart', unlockPlayback, { once: true, passive: true });
    window.addEventListener('pointerdown', unlockPlayback, { once: true, passive: true });
    window.addEventListener('load', refresh);

    if (video.readyState >= 1) handleMetadata();

    return () => {
      mounted = false;
      video.removeEventListener('loadedmetadata', handleMetadata);
      video.removeEventListener('loadeddata', showFirstFrame);
      video.removeEventListener('seeked', handleSeeked);
      window.removeEventListener('touchstart', unlockPlayback);
      window.removeEventListener('pointerdown', unlockPlayback);
      window.removeEventListener('load', refresh);

      if (timeline) {
        if (timeline.scrollTrigger) timeline.scrollTrigger.kill();
        timeline.kill();
        timeline = null;
      }
      gsap.killTweensOf(videoState);
      ctx.revert();
    };
  }, [resolvedSrc, reduced]);

  // Если blob по какой-то причине не проигрывается, откатываемся на обычный файл
  const handleVideoError = () => {
    if (resolvedSrc !== src) setResolvedSrc(src);
  };

  const rootClassName = `${styles.scrollVideo}${reduced ? ` ${styles.scrollVideo_static}` : ''}`;
  const overlayTextClassName = (extra) =>
    `${styles.scrollVideo__text} ${extra}${reduced ? ` ${styles.scrollVideo__text_visible}` : ''}`;

  return (
    <section ref={rootRef} className={rootClassName} aria-label={ariaLabel}>
      <div className={styles.scrollVideo__sticky}>
        <video
          ref={videoRef}
          className={`${styles.scrollVideo__video}${isReady ? ` ${styles.scrollVideo__video_ready}` : ''}`}
          src={resolvedSrc || undefined}
          onError={handleVideoError}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          tabIndex={-1}
          aria-hidden="true"
        />

        <div className={styles.scrollVideo__overlay} aria-hidden="true" />

        <div className={styles.scrollVideo__texts}>
          <p ref={startTextRef} className={styles.scrollVideo__text}>
            <Words text={startText} />
          </p>
          <p ref={middleTextRef} className={overlayTextClassName(styles.scrollVideo__text_layer)}>
            <Words text={middleText} />
          </p>
          <p ref={endTextRef} className={overlayTextClassName(styles.scrollVideo__text_layer)}>
            <Words text={endText} />
          </p>
        </div>

        {!isReady && <span className={styles.scrollVideo__loader} aria-hidden="true" />}
      </div>
    </section>
  );
}
